// @ts-check
/** @jsx jsx */
import { gql } from '@apollo/client';
import { Title } from '@bottlebooks/gatsby-theme-base/src';
import { graphql } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import BaseProductName, { ProductNameFragment } from './ProductName';
import ProductHeaderPrice from './ProductHeaderPrice';
import BaseProductPrice from './ProductPrice/ProductPrice';
import BaseProducerName, {
  ProductProducerNameFragment,
} from './ProductProducerName';
import BaseRegion, { ProductRegionFragment } from './ProductRegion';
import BaseStand, { ProductStandFragment } from './ProductStand';
import BaseVarieties, { ProductVarietiesFragment } from './ProductVarieties';

export {
  ProductNameFragment,
  ProductProducerNameFragment,
  ProductStandFragment,
};

/**
 *
 * @param {object} props
 * @param {{stand?: object, exhibitor?: object, producer?: object}} [props.product]
 * @param {number} [props.gap]
 * @param {import('@bottlebooks/gatsby-design-system/src/components/Title/titleVariants').TitleVariant} [props.titleVariant]
 * @param {string} [props.standPath]
 * @param {boolean} [props.showMorePricingInfo=false] Show more info toggle for price ranges
 * @param {boolean} [props.showStand]
 * @param {boolean} [props.showProducer]
 * @returns
 */
export default function ProductHeader({
  product,
  gap = 3,
  titleVariant = 'small',
  standPath,
  showMorePricingInfo = false,
  showStand = true,
  showProducer = true,
}) {
  const hasOtherExhibitor = hasDifferentExhibitor(product || {});
  return (
    <React.Fragment>
      {showStand && (
        <ProductStand
          stand={product?.stand}
          exhibitor={hasOtherExhibitor && product?.exhibitor}
          to={hasOtherExhibitor && standPath}
        />
      )}
      {showProducer && (
        <ProductProducer
          producer={product?.producer}
          to={!hasOtherExhibitor && standPath}
        />
      )}
      <ProductName
        product={product}
        variant={titleVariant}
        sx={{ paddingBottom: gap }}
      />
      <ProductHeaderDetails
        product={product}
        showMorePricingInfo={showMorePricingInfo}
      />
    </React.Fragment>
  );
}

export function ProductStand({ stand, exhibitor, to, ...rest }) {
  return (
    <BaseStand
      stand={stand}
      exhibitor={exhibitor}
      to={to}
      sx={{ paddingBottom: 3, color: 'lighterText' }}
      {...rest}
    />
  );
}

export function ProductProducer({ producer, to, ...rest }) {
  return <BaseProducerName producer={producer} to={to} {...rest} />;
}

/**
 * @param {object} props
 * @param {object} props.product
 * @param {import('@bottlebooks/gatsby-design-system/src/components/Title/titleVariants').TitleVariant} [props.variant]
 * @param {React.ElementType} [props.as]
 * @returns
 */
export function ProductName({ product, variant, ...rest }) {
  return (
    <Title variant={variant} {...rest}>
      <BaseProductName {...product} />
    </Title>
  );
}

/**
 *
 * @param {object} props
 * @param {object} props.product
 * @param {boolean} [props.showMorePricingInfo=false] Show more info toggle for pricing info
 * @returns
 */
export function ProductHeaderDetails({
  product,
  showMorePricingInfo,
  ...rest
}) {
  return (
    <React.Fragment>
      <ProductRegion product={product} />
      <ProductVarieties product={product} />
      <ProductHeaderPrice
        pricing={product?.pricing}
        showMorePricingInfo={showMorePricingInfo}
        sx={{ paddingTop: 1, color: 'lightText' }}
        {...rest}
      />
    </React.Fragment>
  );
}

export function ProductRegion({ product, ...rest }) {
  return <BaseRegion product={product} sx={{ paddingBottom: 1 }} {...rest} />;
}

export function ProductVarieties({ product, ...rest }) {
  return <BaseVarieties product={product} {...rest} />;
}

/**
 *
 * @param {object} params
 * @param {object} [params.producer]
 * @param {object} [params.exhibitor]
 * @returns {boolean}
 */
export function hasDifferentExhibitor({ producer, exhibitor }) {
  const producerName = producer?.name?.trim();
  const exhibitorName = exhibitor?.name?.trim();
  return Boolean(exhibitorName && producerName !== exhibitorName);
}

export const fragments = graphql`
  fragment hasDifferentExhibitor on Product {
    producer {
      name
    }
    exhibitor {
      name
    }
  }

  # Used in EventProductPageHeader and ProductPageHeader
  fragment ProductHeaderDetails on Product {
    ...ProductVarieties
  }

  # Used in EventProductPageHeader and ProductPageHeader
  fragment bb_ProductHeaderDetails on Bottlebooks_Product {
    ... on Bottlebooks_ExtendedProduct {
      pricing {
        priceType
        ...bb_ProductPrice
      }
      ...bb_ProductRegion
    }
  }

  fragment bb_ProductHeaderDetails_RegisteredProduct on Bottlebooks_RegisteredProduct {
    registration {
      ...bb_ProductStand
      profile {
        name
      }
    }
    pricing {
      priceType
      ...bb_ProductPrice
    }
    product {
      ...bb_ProductName
      ...bb_ProductRegion
      ...bb_ProductProducerName
      ...bb_ProductVarieties
      producer {
        name
      }
    }
  }

  # Used in ProductListRow
  fragment bb_ProductHeader on Bottlebooks_Product {
    ...bb_ProductHeaderDetails
  }

  # Used in ProductListRow
  fragment ProductHeader on Product {
    ...ProductStand
    ...ProductName
    ...ProductProducerName
    ...ProductHeaderDetails
    ...hasDifferentExhibitor
  }
`;

// The client fragments.
export const hasDifferentExhibitorFragment = gql`
  fragment hasDifferentExhibitor on Product {
    producer {
      name
    }
    ... on ExtendedProduct {
      exhibitor {
        name
      }
    }
  }
`;

export const ProductHeaderDetailsFragment = gql`
  fragment ProductHeaderDetails on Product {
    ... on ExtendedProduct {
      pricing {
        priceType
        ...ProductPrice
      }
    }
    ...ProductRegion
    ...ProductVarieties
  }

  ${ProductRegionFragment}
  ${ProductVarietiesFragment}
  ${BaseProductPrice.fragment}
`;

export const ProductHeaderFragment = gql`
  fragment ProductHeader on Product {
    ...ProductStand
    ...ProductName
    ...ProductProducerName
    ...ProductHeaderDetails
    ...hasDifferentExhibitor
  }

  ${ProductStandFragment}
  ${ProductNameFragment}
  ${ProductProducerNameFragment}
  ${ProductHeaderDetailsFragment}
  ${hasDifferentExhibitorFragment}
`;
